/** @jsxImportSource @emotion/react */
import React from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import * as snackbarActions from "../../../../actions/snackbarActions";
import PageTitle from "../../../Utils/smsTitle";
import { SubHeader } from "../../../../Containers/SubHeader";
import MainSnackbar from "../../../Utils/Snackbar/SmsSnackbar";
import ObGynSidebar from "../../Utils/ObGynSidebar";
import ObGynPatientInfo from "../../Utils/ObGynPatientInfo";
import Form from "react-bootstrap/Form";
import { useNewbornRecord } from "./hooks/useNewbornRecord";
import { APGARCalculator } from "./APGARCalculator";
import { history } from "../../../../Helpers/history";
import {
    DRPageWrapper, DRHeader, DRHeaderLeft, DRHeaderTitle, DRHeaderSub,
    DRHeaderStats, DRStatChip,

    DRAlertContainer, DRAlertCard, DRAlertIcon, DRAlertContent,
    DRAlertTitle, DRAlertDetail, DRAlertAction,
    DRFormCard, DRFormHeader, DRFormHeaderLeft, DRFormIcon, DRFormTitle, DRFormBadge,
    DRFormBody, DRFormGrid, DRFormGroup, DRLabel, DRHint,
    DRCheckboxGrid, DRCheckboxItem,
    DRLoading, DRNoAdmission, DRNoAdmissionIcon, DRNoAdmissionTitle,
    DRNoAdmissionDesc, DRPrimaryBtn, DRSecondaryBtn, DRSuccessBtn,
    DRButtonRow,
    DRRecordCard, DRRecordRow, DRRecordLabel, DRRecordValue,
} from "../DeliveryRecord/DeliveryRecordStyles";
import { ObGynTabBar, ObGynTabBtn } from "../../Utils/ObGynTabStyles";

const NewbornRecordPage = ({ actions, snackbar, match }) => {
    const { patient_number, visit_id, patient_type } = match.params;

    const nb = useNewbornRecord(actions, patient_number, visit_id);
    const closeSnackbar = () => { actions.snackbarActions.hideSnackbar(); };

    const goToDelivery = () => {
        history.push("/gyn-and-obs-delivery-record/" + patient_number + "/" + visit_id + "/" + patient_type);
    };

    /* ── Loading ── */
    if (nb.loading) {
        return (
            <div>
                <PageTitle title="Newborn Record" />
                <SubHeader title="OB/GYN" subTitle="Newborn Assessment" />
                <MainSnackbar variant={snackbar.variant} message={snackbar.message}
                    open={snackbar.open} handleCloseBar={closeSnackbar} />
                <ObGynSidebar patient_number={patient_number} visit_id={visit_id} patient_type={patient_type}>
                    <ObGynPatientInfo patient_number={patient_number} visit_id={visit_id} patient_type={patient_type} />
                    <DRPageWrapper>
                        <DRLoading>⏳ Loading newborn data...</DRLoading>
                    </DRPageWrapper>
                </ObGynSidebar>
            </div>
        );
    }

    /* ── No delivery record ── */
    if (!nb.deliveryRecord) {
        return (
            <div>
                <PageTitle title="Newborn Record" />
                <SubHeader title="OB/GYN" subTitle="Newborn Assessment" />
                <MainSnackbar variant={snackbar.variant} message={snackbar.message}
                    open={snackbar.open} handleCloseBar={closeSnackbar} />
                <ObGynSidebar patient_number={patient_number} visit_id={visit_id} patient_type={patient_type}>
                    <ObGynPatientInfo patient_number={patient_number} visit_id={visit_id} patient_type={patient_type} />
                    <DRPageWrapper>
                        <DRNoAdmission>
                            <DRNoAdmissionIcon>👶</DRNoAdmissionIcon>
                            <DRNoAdmissionTitle>No Delivery Record Found</DRNoAdmissionTitle>
                            <DRNoAdmissionDesc>
                                Please record the delivery first before creating newborn assessments.
                            </DRNoAdmissionDesc>
                            <DRPrimaryBtn onClick={goToDelivery}>Go to Delivery Record</DRPrimaryBtn>
                        </DRNoAdmission>
                    </DRPageWrapper>
                </ObGynSidebar>
            </div>
        );
    }

    /* ── Assessment Form Tab ── */
    const renderAssessmentForm = () => {
        const { form } = nb;
        return (
            <div>
                {/* Editing banner */}
                {nb.editingNewborn ? (
                    <div style={{
                        padding: "10px 16px",
                        background: "#fff3e0",
                        borderRadius: "8px",
                        border: "1px solid #ffe0b2",
                        marginBottom: "14px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                    }}>
                        <span style={{ fontWeight: 600, color: "#e65100", fontSize: "0.85rem" }}>
                            ✏️ Editing Baby #{nb.editingNewborn.birth_order || 1} Record
                        </span>
                        <DRSecondaryBtn style={{ padding: "4px 12px", fontSize: "0.78rem" }}
                            onClick={nb.cancelEdit}>
                            Cancel Edit
                        </DRSecondaryBtn>
                    </div>
                ) : null}

                {/* Section 1: Basic Info */}
                <DRFormCard color="#283593">
                    <DRFormHeader>
                        <DRFormHeaderLeft>
                            <DRFormIcon>👶</DRFormIcon>
                            <DRFormTitle>Newborn Information</DRFormTitle>
                        </DRFormHeaderLeft>
                    </DRFormHeader>
                    <DRFormBody>
                        <DRFormGrid cols={4}>
                            <DRFormGroup>
                                <DRLabel>Birth Order</DRLabel>
                                <Form.Control
                                    type="number" size="sm" name="birth_order"
                                    value={form.birth_order || 1} onChange={nb.handleChange} min={1} max={6}
                                />
                            </DRFormGroup>
                            <DRFormGroup>
                                <DRLabel required>Sex</DRLabel>
                                <Form.Control
                                    as="select" size="sm" name="sex"
                                    value={form.sex || ""} onChange={nb.handleChange}
                                >
                                    <option value="">— Select —</option>
                                    {nb.SEX_OPTIONS.map((o) => (
                                        <option key={o.value} value={o.value}>{o.label}</option>
                                    ))}
                                </Form.Control>
                            </DRFormGroup>
                            <DRFormGroup>
                                <DRLabel required>Birth Weight (grams)</DRLabel>
                                <Form.Control
                                    type="number" size="sm" name="birth_weight_grams"
                                    value={form.birth_weight_grams || ""} onChange={nb.handleChange}
                                    placeholder="e.g., 3200" min={200} max={7000}
                                />
                                {nb.weightClass
                                    ? <DRHint warn={nb.weightClass.color !== "#2e7d32"}>{nb.weightClass.label}</DRHint>
                                    : null}
                            </DRFormGroup>
                            <DRFormGroup>
                                <DRLabel>Birth Length (cm)</DRLabel>
                                <Form.Control
                                    type="number" size="sm" name="birth_length_cm"
                                    value={form.birth_length_cm || ""} onChange={nb.handleChange}
                                    placeholder="e.g., 50" min={20} max={65}
                                />
                            </DRFormGroup>
                        </DRFormGrid>
                        <DRFormGrid cols={3} style={{ marginTop: "10px" }}>
                            <DRFormGroup>
                                <DRLabel>Head Circumference (cm)</DRLabel>
                                <Form.Control
                                    type="number" size="sm" name="head_circumference_cm"
                                    value={form.head_circumference_cm || ""} onChange={nb.handleChange}
                                    placeholder="e.g., 35" min={20} max={50}
                                />
                            </DRFormGroup>
                            <DRFormGroup>
                                <DRLabel>Outcome</DRLabel>
                                <Form.Control
                                    as="select" size="sm" name="outcome"
                                    value={form.outcome || "alive"} onChange={nb.handleChange}
                                >
                                    {nb.OUTCOME_OPTIONS.map((o) => (
                                        <option key={o.value} value={o.value}>{o.label}</option>
                                    ))}
                                </Form.Control>
                                {form.outcome !== "alive"
                                    ? <DRHint warn>{"⚠ " + (
                                        form.outcome === "fresh_stillbirth" ? "Fresh stillbirth" :
                                        form.outcome === "macerated_stillbirth" ? "Macerated stillbirth" :
                                        "Neonatal death")}</DRHint>
                                    : null}
                            </DRFormGroup>
                            {form.outcome === "neonatal_death" ? (
                                <DRFormGroup>
                                    <DRLabel>Time of Death</DRLabel>
                                    <Form.Control
                                        type="datetime-local" size="sm" name="death_time"
                                        value={form.death_time || ""} onChange={nb.handleChange}
                                    />
                                </DRFormGroup>
                            ) : null}
                        </DRFormGrid>
                    </DRFormBody>
                </DRFormCard>

                {/* Section 2: APGAR */}
                <APGARCalculator nb={nb} show10min={form.outcome !== "alive" || (nb.apgarTotals["5min"].complete && nb.apgarTotals["5min"].total < 7)} />

                {/* Section 3: Resuscitation */}
                <DRFormCard color="#c62828">
                    <DRFormHeader>
                        <DRFormHeaderLeft>
                            <DRFormIcon>🫁</DRFormIcon>
                            <DRFormTitle>Resuscitation</DRFormTitle>
                        </DRFormHeaderLeft>
                        {form.resuscitation_needed
                            ? <DRFormBadge variant="warning">Required</DRFormBadge>
                            : <DRFormBadge variant="success">Not needed</DRFormBadge>}
                    </DRFormHeader>
                    <DRFormBody>
                        <DRFormGrid cols={2}>
                            <DRFormGroup>
                                <DRLabel>Resuscitation Needed?</DRLabel>
                                <Form.Control
                                    as="select" size="sm" name="resuscitation_needed"
                                    value={form.resuscitation_needed ? "true" : "false"}
                                    onChange={(ev) => {
                                        nb.handleChange({
                                            target: { name: "resuscitation_needed", value: ev.target.value === "true", type: "select" },
                                        });
                                    }}
                                >
                                    <option value="false">No</option>
                                    <option value="true">Yes</option>
                                </Form.Control>
                            </DRFormGroup>
                        </DRFormGrid>
                        {form.resuscitation_needed ? (
                            <div style={{ marginTop: "10px" }}>
                                <DRLabel>Type(s) of Resuscitation</DRLabel>
                                <DRCheckboxGrid cols={3}>
                                    {nb.RESUSCITATION_TYPES.map((t) => {
                                        const checked = (form.resuscitation_type || []).indexOf(t.value) >= 0;
                                        return (
                                            <DRCheckboxItem
                                                key={t.value} checked={checked}
                                                onClick={(ev) => { ev.preventDefault(); nb.toggleResuscitationType(t.value); }}
                                            >
                                                <input type="checkbox" checked={checked} readOnly style={{ accentColor: "#c62828" }} />
                                                {t.label}
                                            </DRCheckboxItem>
                                        );
                                    })}
                                </DRCheckboxGrid>
                            </div>
                        ) : null}
                    </DRFormBody>
                </DRFormCard>

                {/* Section 4: Immediate Care */}
                <DRFormCard color="#2e7d32">
                    <DRFormHeader>
                        <DRFormHeaderLeft>
                            <DRFormIcon>🤱</DRFormIcon>
                            <DRFormTitle>Immediate Newborn Care</DRFormTitle>
                        </DRFormHeaderLeft>
                        <DRFormBadge variant="success">
                            {nb.immediateCareItems.filter((i) => i.checked).length + "/" +
                            nb.immediateCareItems.length + " done"}
                        </DRFormBadge>
                    </DRFormHeader>
                    <DRFormBody>
                        {nb.immediateCareItems.map((item) => (
                            <label
                                key={item.key}
                                style={{
                                    display: "flex", alignItems: "center", gap: "10px",
                                    padding: "10px 14px", borderRadius: "8px", marginBottom: "6px",
                                    background: item.checked ? "#e8f5e9" : "#fafafa",
                                    border: "1px solid " + (item.checked ? "#c8e6c9" : "#eee"),
                                    cursor: "pointer",
                                }}
                            >
                                <input
                                    type="checkbox" checked={item.checked}
                                    name={item.key}
                                    onChange={nb.handleChange}
                                    style={{ accentColor: "#2e7d32", width: "18px", height: "18px" }}
                                />
                                <span style={{ fontSize: "1.1rem" }}>{item.icon}</span>
                                <span style={{
                                    fontWeight: item.checked ? 600 : 400,
                                    color: item.checked ? "#2e7d32" : "#666",
                                    fontSize: "0.85rem",
                                }}>{item.label}</span>
                            </label>
                        ))}
                        <DRFormGrid cols={3} style={{ marginTop: "12px" }}>
                            {form.skin_to_skin_initiated ? (
                                <DRFormGroup>
                                    <DRLabel>Skin-to-Skin Time</DRLabel>
                                    <Form.Control type="time" size="sm" name="skin_to_skin_time" value={form.skin_to_skin_time || ""} onChange={nb.handleChange} />
                                </DRFormGroup>
                            ) : null}
                            {form.breastfeeding_within_1hr ? (
                                <DRFormGroup>
                                    <DRLabel>Breastfeeding Time</DRLabel>
                                    <Form.Control type="time" size="sm" name="breastfeeding_time" value={form.breastfeeding_time || ""} onChange={nb.handleChange} />
                                </DRFormGroup>
                            ) : null}
                            <DRFormGroup>
                                <DRLabel>Cord Care</DRLabel>
                                <Form.Control
                                    as="select" size="sm" name="cord_care"
                                    value={form.cord_care || ""} onChange={nb.handleChange}
                                >
                                    {nb.CORD_CARE_OPTIONS.map((o) => (
                                        <option key={o.value} value={o.value}>{o.label}</option>
                                    ))}
                                </Form.Control>
                            </DRFormGroup>
                        </DRFormGrid>
                        <DRFormGrid cols={2} style={{ marginTop: "10px" }}>
                            <DRFormGroup>
                                <DRLabel>Feeding Option</DRLabel>
                                <Form.Control
                                    as="select" size="sm" name="feeding_option"
                                    value={form.feeding_option || ""} onChange={nb.handleChange}
                                >
                                    {nb.FEEDING_OPTIONS.map((o) => (
                                        <option key={o.value} value={o.value}>{o.label}</option>
                                    ))}
                                </Form.Control>
                            </DRFormGroup>
                        </DRFormGrid>
                    </DRFormBody>
                </DRFormCard>

                {/* Section 5: Birth Vaccinations */}
                <DRFormCard color="#0277bd">
                    <DRFormHeader>
                        <DRFormHeaderLeft>
                            <DRFormIcon>💉</DRFormIcon>
                            <DRFormTitle>Birth Vaccinations</DRFormTitle>
                        </DRFormHeaderLeft>
                        <DRFormBadge variant="success">
                            {nb.vaccinationItems.filter((i) => i.checked).length + "/" +
                            nb.vaccinationItems.length + " given"}
                        </DRFormBadge>
                    </DRFormHeader>
                    <DRFormBody>
                        {nb.vaccinationItems.map((item) => (
                            <label
                                key={item.key}
                                style={{
                                    display: "flex", alignItems: "center", gap: "10px",
                                    padding: "10px 14px", borderRadius: "8px", marginBottom: "6px",
                                    background: item.checked ? "#e3f2fd" : "#fafafa",
                                    border: "1px solid " + (item.checked ? "#bbdefb" : "#eee"),
                                    cursor: "pointer",
                                }}
                            >
                                <input
                                    type="checkbox" checked={item.checked}
                                    name={item.key} onChange={nb.handleChange}
                                    style={{ accentColor: "#0277bd", width: "18px", height: "18px" }}
                                />
                                <span style={{ fontSize: "1.1rem" }}>{item.icon}</span>
                                <span style={{
                                    fontWeight: item.checked ? 600 : 400,
                                    color: item.checked ? "#0277bd" : "#666",
                                    fontSize: "0.85rem",
                                }}>{item.label}</span>
                            </label>
                        ))}
                    </DRFormBody>
                </DRFormCard>

                {/* Section 6: Physical Exam Notes */}
                <DRFormCard color="#6a1b9a">
                    <DRFormHeader>
                        <DRFormHeaderLeft>
                            <DRFormIcon>📝</DRFormIcon>
                            <DRFormTitle>Physical Examination & Notes</DRFormTitle>
                        </DRFormHeaderLeft>
                    </DRFormHeader>
                    <DRFormBody>
                        <DRFormGroup>
                            <DRLabel>Congenital Anomalies</DRLabel>
                            <Form.Control
                                as="textarea" size="sm" rows={2} name="congenital_anomalies"
                                value={form.congenital_anomalies || ""} onChange={nb.handleChange}
                                placeholder="Note any congenital anomalies observed (or 'None')"
                            />
                        </DRFormGroup>
                        <DRFormGroup style={{ marginTop: "10px" }}>
                            <DRLabel>Physical Exam Notes</DRLabel>
                            <Form.Control
                                as="textarea" size="sm" rows={3} name="physical_exam_notes"
                                value={form.physical_exam_notes || ""} onChange={nb.handleChange}
                                placeholder="General appearance, activity, tone, cry, color..."
                            />
                        </DRFormGroup>
                    </DRFormBody>
                </DRFormCard>

                {/* Action Buttons */}
                <DRButtonRow>
                    {nb.editingNewborn
                        ? <DRSecondaryBtn onClick={nb.cancelEdit}>Cancel Edit</DRSecondaryBtn>
                        : null}
                    <DRPrimaryBtn
                        disabled={nb.saving || !form.sex || !form.birth_weight_grams}
                        onClick={nb.saveNewborn}
                    >
                        {nb.saving ? "Saving..." : (nb.editingNewborn ? "Update Newborn Record" : "Save Newborn Record")}
                    </DRPrimaryBtn>
                </DRButtonRow>
            </div>
        );
    };

    /* ── Newborn List Tab ── */
    const renderListTab = () => {
        if (nb.newborns.length === 0) {
            return (
                <DRNoAdmission>
                    <DRNoAdmissionIcon>👶</DRNoAdmissionIcon>
                    <DRNoAdmissionTitle>No Newborn Records Yet</DRNoAdmissionTitle>
                    <DRNoAdmissionDesc>
                        Click "New Assessment" to record a newborn assessment.
                    </DRNoAdmissionDesc>
                    <DRPrimaryBtn onClick={() => { nb.setActiveTab("assessment"); }}>
                        New Assessment
                    </DRPrimaryBtn>
                </DRNoAdmission>
            );
        }

        return (
            <div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "14px" }}>
                    <h4 style={{ margin: 0, fontSize: "1rem", fontWeight: 700, color: "#283593" }}>
                        Recorded Newborns ({nb.newborns.length})
                    </h4>
                    <DRPrimaryBtn onClick={() => { nb.cancelEdit(); nb.setActiveTab("assessment"); }}
                        style={{ padding: "6px 16px", fontSize: "0.8rem" }}>
                        + New Assessment
                    </DRPrimaryBtn>
                </div>
                {nb.newborns.map((baby, idx) => (
                    <DRRecordCard key={baby.id || idx}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
                            <h5 style={{ margin: 0, fontSize: "0.95rem", fontWeight: 700, color: "#283593" }}>
                                👶 Baby #{baby.birth_order || (idx + 1)} —{" "}
                                {baby.sex === "male" ? "♂ Male" : baby.sex === "female" ? "♀ Female" : "Ambiguous"}
                            </h5>
                            <DRSecondaryBtn style={{ padding: "4px 12px", fontSize: "0.78rem" }}
                                onClick={() => { nb.startEditNewborn(baby); }}>
                                ✏️ Edit
                            </DRSecondaryBtn>
                        </div>
                        <DRRecordRow>
                            <DRRecordLabel>Birth Weight</DRRecordLabel>
                            <DRRecordValue warn={parseFloat(baby.birth_weight_grams) < 2500}>
                                {baby.birth_weight_grams}g
                            </DRRecordValue>
                        </DRRecordRow>
                        <DRRecordRow>
                            <DRRecordLabel>APGAR (1min / 5min)</DRRecordLabel>
                            <DRRecordValue warn={baby.apgar_5min !== null && baby.apgar_5min < 7}>
                                {baby.apgar_1min !== null ? baby.apgar_1min : "—"} / {baby.apgar_5min !== null ? baby.apgar_5min : "—"}
                            </DRRecordValue>
                        </DRRecordRow>
                        <DRRecordRow>
                            <DRRecordLabel>Outcome</DRRecordLabel>
                            <DRRecordValue warn={baby.outcome !== "alive"}>
                                {baby.outcome === "alive" ? "✓ Live Birth" :
                                 baby.outcome === "fresh_stillbirth" ? "Fresh Stillbirth" :
                                 baby.outcome === "macerated_stillbirth" ? "Macerated Stillbirth" :
                                 "Neonatal Death"}
                            </DRRecordValue>
                        </DRRecordRow>
                        <DRRecordRow>
                            <DRRecordLabel>Resuscitation</DRRecordLabel>
                            <DRRecordValue>{baby.resuscitation_needed ? "Yes" : "No"}</DRRecordValue>
                        </DRRecordRow>
                    </DRRecordCard>
                ))}
            </div>
        );
    };

    return (
        <div>
            <PageTitle title="Newborn Record" />
            <SubHeader title="OB/GYN" subTitle="Newborn Assessment & APGAR" />
            <MainSnackbar variant={snackbar.variant} message={snackbar.message}
                open={snackbar.open} handleCloseBar={closeSnackbar} />
            <ObGynSidebar patient_number={patient_number} visit_id={visit_id} patient_type={patient_type}>
                <ObGynPatientInfo patient_number={patient_number} visit_id={visit_id} patient_type={patient_type} />
                <DRPageWrapper>
                    {/* Header */}
                    <DRHeader>
                        <DRHeaderLeft>
                            <DRHeaderTitle>Newborn Assessment & APGAR</DRHeaderTitle>
                            <DRHeaderSub>
                                Delivery: {nb.deliveryRecord.delivery_mode
                                    ? nb.deliveryRecord.delivery_mode.toUpperCase().replace("_", " ")
                                    : "—"}{" "}
                                | Babies Expected: {nb.deliveryRecord.number_of_babies || 1}
                                | Recorded: {nb.newborns.length}
                            </DRHeaderSub>
                        </DRHeaderLeft>
                        <DRHeaderStats>
                            <DRStatChip variant="success">
                                 {nb.newborns.length} Recorded
                            </DRStatChip>
                            {/* {nb.alerts.length > 0 ? (
                                <DRStatChip variant="danger">
                                    ⚠ {nb.alerts.length} Alert{nb.alerts.length > 1 ? "s" : ""}
                                </DRStatChip>
                            ) : null} */}
                        </DRHeaderStats>
                    </DRHeader>

                    {/* Alerts */}
                    {/* {nb.alerts.length > 0 && nb.activeTab === "assessment" ? (
                        <DRAlertContainer>
                            {nb.alerts.map((alert, idx) => (
                                <DRAlertCard key={idx} severity={alert.type}>
                                    <DRAlertIcon>{alert.type === "critical" ? "🚨" : "⚠️"}</DRAlertIcon>
                                    <DRAlertContent>
                                        <DRAlertTitle>{alert.title}</DRAlertTitle>
                                        <DRAlertDetail>{alert.detail}</DRAlertDetail>
                                        <DRAlertAction>{alert.action}</DRAlertAction>
                                    </DRAlertContent>
                                </DRAlertCard>
                            ))}
                        </DRAlertContainer>
                    ) : null} */}

                    {/* Tabs */}
                    <ObGynTabBar>
                        <ObGynTabBtn active={nb.activeTab === "assessment"}
                            onClick={() => { nb.setActiveTab("assessment"); }}>
                            Assessment Form
                        </ObGynTabBtn>
                        <ObGynTabBtn active={nb.activeTab === "list"}
                            onClick={() => { nb.setActiveTab("list"); }}>
                            Recorded Newborns ({nb.newborns.length})
                        </ObGynTabBtn>
                    </ObGynTabBar>

                    {/* Tab content */}
                    {nb.activeTab === "assessment" ? renderAssessmentForm() : null}
                    {nb.activeTab === "list" ? renderListTab() : null}
                </DRPageWrapper>
            </ObGynSidebar>
        </div>
    );
};

const mapStateToProps = (state) => ({
    snackbar: state.snackbar,
});

const mapDispatchToProps = (dispatch) => ({
    actions: { snackbarActions: bindActionCreators(snackbarActions, dispatch) },
});

export default connect(mapStateToProps, mapDispatchToProps)(NewbornRecordPage);
